import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

// API base URL - use relative URLs for production compatibility
axios.defaults.baseURL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080';
axios.defaults.withCredentials = true;

interface User {
  id: string;
  username: string;
  email?: string;
  name?: string;
  picture?: string;
  instagramId?: string;
  instagramBusinessId?: string;
  instagramUsername?: string;
  facebookId?: string;
  facebookPageId?: string;
  facebookPageName?: string;
  pageId?: string;
  webhookId?: string;
  tiktokId?: string;
  tiktokUsername?: string;
  tiktokCompany?: string;
  tiktokEmail?: string;
  tiktokPhone?: string;
  tiktokFollowerCount?: number;
  tiktokVideoCount?: number;
  whatsappBusinessAccountId?: string;
  whatsappPhoneNumber?: string;
  whatsappAccessToken?: string;
  // Subscription fields
  subscriptionPlan?: 'free' | 'trial' | 'starter' | 'professional' | 'enterprise';
  subscriptionStatus?: 'active' | 'inactive' | 'canceled' | 'past_due' | 'trialing';
  subscriptionDate?: string;
  subscriptionEndDate?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  // Trial fields
  isTrialing?: boolean;
  trialStartDate?: string;
  trialEndDate?: string;
  trialUsed?: boolean;
  authType?: 'user' | 'loginUser' | 'session';
  created_at?: string;
  updated_at?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  signup: (email: string, password: string) => Promise<boolean>;
  googleAuth: (accessToken: string) => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  updateSubscriptionLocally: (plan: 'starter' | 'professional' | 'enterprise', status?: 'active' | 'inactive' | 'canceled' | 'past_due') => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await axios.post('/api/auth/login', { username: email, password });
      setUser(response.data.user);
      
      // Login başarılı olduktan sonra /me endpoint'ini çağır (otomatik bağlantı için)
      setTimeout(() => {
        checkAuth();
      }, 100);
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const signup = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await axios.post('/api/auth/signup', { email, password });
      setUser(response.data.user);
      return true;
    } catch (error) {
      console.error('Signup error:', error);
      return false;
    }
  };

  const googleAuth = async (accessToken: string): Promise<boolean> => {
    try {
      const response = await axios.post('/api/auth/google', { access_token: accessToken });
      setUser(response.data.user);
      
      // Google auth başarılı olduktan sonra /me endpoint'ini çağır (otomatik bağlantı için)
      setTimeout(() => {
        checkAuth();
      }, 100);
      
      return true;
    } catch (error) {
      console.error('Google auth error:', error);
      return false;
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout');
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const updateSubscriptionLocally = (plan: 'starter' | 'professional' | 'enterprise', status: 'active' | 'inactive' | 'canceled' | 'past_due' = 'active') => {
    setUser((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        subscriptionPlan: plan,
        subscriptionStatus: status,
        subscriptionDate: new Date().toISOString(),
      };
    });
  };

  useEffect(() => {
    checkAuth();
    // Expose for places that need to refresh after server-side updates without prop drilling
    (window as any).checkAuth = checkAuth;
  }, []);

  const value = {
    user,
    loading,
    login,
    signup,
    googleAuth,
    logout,
    checkAuth,
    updateSubscriptionLocally,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 